import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AlertDialogComponent,
  AlertDialogData,
} from 'src/app/modules/shared/components/dialogs/alert-dialog/alert-dialog.component';
import { AuthenticationService } from 'src/app/pages/auth/services/authentication.service';
import { AuthService } from 'src/app/pages/core/abstraction/auth.service';
import { cities, provinces } from 'src/app/pages/core/utils/cities';
import errors from 'src/app/pages/core/utils/errorCodes';

@Component({
  selector: 'app-edit-information',
  templateUrl: './edit-information.component.html',
  styleUrls: ['./edit-information.component.scss'],
})
export class EditInformationComponent implements OnInit {
  phoneControl = new FormControl('', [
    Validators.required,
    this.phoneLengthValidator.bind(this),
    Validators.maxLength(10),
  ]);
  addressStreetControl = new FormControl('', [Validators.required]);
  addressNumberControl = new FormControl('', [Validators.required]);
  addressFloorControl = new FormControl('');
  cityControl = new FormControl('', [Validators.required]);
  postalControl = new FormControl('', [Validators.required, this.minLengthPostalCode]);
  provinceControl = new FormControl(undefined, [Validators.required]);

  controlNames = {
    addressStreet: this.addressStreetControl,
    addressNumber: this.addressNumberControl,
    addressFloor: this.addressFloorControl,
    locality: this.cityControl,
    postalCode: this.postalControl,
    province: this.provinceControl,
    phone: this.phoneControl,
  };

  provinces = provinces;
  cities = cities;
  options: string[] = ['Selecciona primero la provincia'];

  constructor(
    public auth: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<EditInformationComponent>,
    public authentication: AuthenticationService,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.options = this.cities[0];
    this.fillInputsWithUserData();
  }

  onNoClick() {
    this.dialogRef.close(false);
  }

  askForPhone() {
    return this.auth.complianceChecks?.expiredPhone;
  }

  askForAddress() {
    return this.auth.complianceChecks?.expiredAddress;
  }

  fillInputsWithUserData() {
    this.options =
      this.cities[this.provinces.findIndex((a) => a === this.auth.loggedUser.data.province)];
    let userData = {
      addressStreet: this.auth.loggedUser?.data?.street,
      addressNumber: this.auth.loggedUser?.data?.numeration,
      addressFloor: this.auth.loggedUser?.data?.department,
      locality: this.auth.loggedUser?.data?.city,
      postalCode: this.auth.loggedUser?.data?.postalCode,
      province: this.auth.loggedUser?.data?.province,
      phone: this.auth.loggedUser?.phoneNumber,
    };
    for (let i = 0; i < Object.keys(this.controlNames).length; i++) {
      this.controlNames[Object.keys(this.controlNames)[i]].setValue(
        userData[Object.keys(this.controlNames)[i]],
      );
    }
  }

  phoneLengthValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && control.value.toString().length < 10) {
      return { phoneShort: true };
    }
    return null;
  }

  minLengthPostalCode(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && control.value.length < 4) {
      return { minlengthfour: true };
    }
    return null;
  }

  citySelector() {
    this.cityControl.setValue('');
    this.cityControl.markAsTouched();
    this.cityControl.markAsDirty();
    this.options = this.cities[this.provinces.findIndex((a) => a === this.provinceControl.value)];
  }

  invalidForm() {
    const controls = Object.values(this.controlNames);

    controls.forEach((control) => {
      control.markAsDirty();
      control.markAsTouched();
    });

    return controls.filter((control) => control.invalid).length > 0;
  }

  addInformation() {
    if (this.invalidForm()) return;

    const addressFields = {
      street: this.addressStreetControl.value,
      numeration: this.addressNumberControl.value.toString(),
      department: this.addressFloorControl.value,
      province: this.provinceControl.value,
      city: this.cityControl.value,
      postalCode: this.postalControl.value.toString(),
    };
    const phoneNumber = this.phoneControl.value.toString();
    let fields = {};
    if (this.auth.complianceChecks?.expiredAddress && this.auth.complianceChecks.expiredPhone) {
      fields = {
        ...addressFields,
        ...{ phoneNumber },
      };
    } else if (this.auth.complianceChecks?.expiredAddress) {
      fields = {
        ...addressFields,
      };
    } else if (this.auth.complianceChecks.expiredPhone) {
      fields = {
        ...{ phoneNumber },
      };
    }
    this.authentication.infoUpdate(fields, true).subscribe(
      () => {
        if (this.auth.complianceChecks?.expiredAddress) {
          this.auth.complianceChecks.expiredAddress = !this.auth.complianceChecks.expiredAddress;
          this.auth.loggedUser.data.street = this.addressStreetControl.value;
          this.auth.loggedUser.data.numeration = this.addressNumberControl.value;
          this.auth.loggedUser.data.department = this.addressFloorControl.value;
          this.auth.loggedUser.data.city = this.cityControl.value;
          this.auth.loggedUser.data.postalCode = this.postalControl.value;
          this.auth.loggedUser.data.province = this.provinceControl.value;
        }
        if (this.auth.complianceChecks?.expiredPhone) {
          this.auth.complianceChecks.expiredPhone = !this.auth.complianceChecks.expiredPhone;
          this.auth.loggedUser.phoneNumber = this.phoneControl.value;
        }
        this.dialogRef.close(true);
      },
      (error) => {
        this.dialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
          panelClass: 'modal-container',
          data: {
            title: 'Error al confirmar los datos',
            content: 'No pudimos confirmar tus datos, intentalo de nuevo más tarde.',
            errorCode: error.error.internalStatus,
            isError: true,
          },
        });
      },
    );
  }

  getErrorMessage(input) {
    const keys = Object.keys(this.controlNames[input]?.errors || {});
    if (keys.length === 0) {
      return '';
    }
    return errors[keys[0]];
  }
}
